<template>
  <div
    class="play-avatar-group"
    :style="groupStyle"
  >
    <slot />
  </div>
</template>

<script>
/**
 * PlayAvatarGroup — overlapping stack of <PlayAvatar>s for the Play system.
 *
 * Token-driven (no hardcoded colors). Replaces bootstrap-vue's
 * <b-avatar-group>. Provides its `size` to child <PlayAvatar>s (set it once on
 * the group) and auto-rings them so overlapping avatars stay separated; each
 * avatar lifts on hover. `overlap` (0–1) is how much each avatar covers the
 * previous one.
 *
 * Coordination is via provide/inject (NOT $parent), so it is Vue 3-safe.
 */
export default {
  name: 'PlayAvatarGroup',
  provide() {
    return {
      playAvatarGroup: this.groupApi,
    }
  },
  props: {
    /** Child avatar size — number (px) | 'sm'|'md'|'lg'|'xl' | raw CSS length */
    size: {
      type: [Number, String],
      default: 'md',
    },
    /** Fraction (0–1) each avatar overlaps the previous one */
    overlap: {
      type: Number,
      default: 0.35,
    },
  },
  computed: {
    /** Shared contract handed to child PlayAvatars via inject. */
    groupApi() {
      return { size: this.size }
    },
    groupStyle() {
      return { '--pag-overlap': this.overlap }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-avatar-group {
  display: inline-flex;
  align-items: center;

  /* Pull each avatar (after the first) over the previous by size × overlap;
     lift on hover. ::v-deep reaches the child PlayAvatar root elements. */
  ::v-deep > .play-avatar {
    transition: transform var(--dur-fast) var(--ease-bounce);

    & + .play-avatar {
      margin-inline-start: calc(var(--av-size, 38px) * var(--pag-overlap) * -1);
    }

    &:hover {
      transform: translateY(-4px);
      z-index: 2;
    }
  }
}
</style>
